import React, { useState, useRef, useEffect } from 'react';
// @redux
import { connect, useSelector } from 'react-redux';
// @material-ui
import { makeStyles } from '@material-ui/core/styles';
import { TextField, Box, Card, Button, Typography, Icon } from '@material-ui/core';

// @third-party
import clsx from 'clsx';
import Markdown from 'react-markdown'
import axios from 'axios';

// @components
import ResultRenderer from './ResultRenderer';
import DUMMY_RESPONSES, { DEMO_KEYS } from './chatTaliDummies';


const useStyles = makeStyles((theme) => ({
    '@keyframes taliTyping': {
        '0%, 60%, 100%': { transform: 'translateY(0)', opacity: 0.4 },
        '30%': { transform: 'translateY(-5px)', opacity: 1 },
    },
    root: {
        height: 'auto',
        width: '100%',
        maxWidth: '80%',
        margin: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        padding: '16px',
        borderRadius: '16px',
        backgroundColor: theme.palette.colors.darkBlack[400],
    },
    shell: {
        display: 'flex',
        flexDirection: 'column',
        height: '78vh',
        borderRadius: '16px',
        overflow: 'hidden',
        border: '1px solid ' + theme.palette.colors.fillStroke[200],
        backgroundColor: theme.palette.colors.fillStroke[400],
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '0 24px',
        height: '72px',
        background: `linear-gradient(90deg, ${theme.palette.colors.violet[400]} 0%, ${theme.palette.colors.violet[300]} 100%)`,
    },
    headerAvatar: {
        width: '40px',
        height: '40px',
        borderRadius: '50%',
        backgroundColor: theme.palette.white,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    headerTitle: {
        color: theme.palette.white,
        fontWeight: 700,
        lineHeight: 1.2,
    },
    headerStatusRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
    },
    statusDot: {
        width: '8px',
        height: '8px',
        borderRadius: '50%',
        backgroundColor: theme.palette.colors.success[300],
    },
    headerSub: {
        color: theme.palette.colors.violet[100],
        fontSize: '12px',
    },
    container: {
        flex: 1,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
    },
    chatContainer: {
        flex: 1,
        overflowY: 'auto',
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        backgroundColor: theme.palette.colors.fillStroke[400],
    },
    messageRow: {
        display: 'flex',
        gap: '10px',
        alignItems: 'flex-start',
        width: '100%',
    },
    userRow: {
        justifyContent: 'flex-end',
    },
    assistantRow: {
        justifyContent: 'flex-start',
    },
    botAvatar: {
        width: '32px',
        height: '32px',
        borderRadius: '50%',
        backgroundColor: theme.palette.colors.violet[300],
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        marginTop: '2px',
    },
    messageBubble: {
        maxWidth: '70%',
        padding: '12px 16px',
        borderRadius: '16px',
        wordWrap: 'break-word',
        position: 'relative',
        '& p': {
            margin: 0,
        },
    },
    userBubble: {
        background: `linear-gradient(90deg, ${theme.palette.colors.violet[400]} 0%, ${theme.palette.colors.violet[300]} 100%)`,
        color: theme.palette.white,
        borderBottomRightRadius: '4px',
    },
    assistantBubble: {
        backgroundColor: theme.palette.colors.fillStroke[300],
        color: theme.palette.colors.gray[100],
        border: '1px solid ' + theme.palette.colors.fillStroke[200],
        borderBottomLeftRadius: '4px',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
    },
    messageLabel: {
        fontSize: '11px',
        fontWeight: 700,
        letterSpacing: '1px',
        marginBottom: '2px',
    },
    assistantLabel: {
        color: theme.palette.colors.violet[300],
    },
    typingBubble: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: '5px',
        padding: '14px 16px',
    },
    typingDot: {
        width: '7px',
        height: '7px',
        borderRadius: '50%',
        backgroundColor: theme.palette.colors.violet[300],
        display: 'inline-block',
        animation: '$taliTyping 1.4s infinite ease-in-out',
    },
    typingDot1: { animationDelay: '0s' },
    typingDot2: { animationDelay: '0.2s' },
    typingDot3: { animationDelay: '0.4s' },
    confirmBubble: {
        maxWidth: '70%',
        padding: '16px',
        borderRadius: '16px',
        borderBottomLeftRadius: '4px',
        backgroundColor: theme.palette.colors.fillStroke[300],
        border: '1px solid ' + theme.palette.colors.warning[300],
        display: 'flex',
        flexDirection: 'column',
        gap: '14px',
    },
    confirmHead: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    confirmHeadLabel: {
        color: theme.palette.colors.warning[300],
        fontSize: '12px',
        fontWeight: 700,
    },
    confirmMessage: {
        color: theme.palette.colors.gray[100],
        fontSize: '14px',
        lineHeight: 1.5,
    },
    confirmButton: {
        alignSelf: 'flex-start',
        borderRadius: '10px',
        padding: '8px 20px',
        background: `linear-gradient(90deg, ${theme.palette.colors.violet[400]} 0%, ${theme.palette.colors.violet[300]} 100%)`,
        color: theme.palette.white,
        '&:hover': {
            background: `linear-gradient(90deg, ${theme.palette.colors.violet[500]} 0%, ${theme.palette.colors.violet[400]} 100%)`,
        },
    },
    inputContainer: {
        display: 'flex',
        gap: '12px',
        alignItems: 'center',
        borderTop: '1px solid ' + theme.palette.colors.fillStroke[200],
        padding: '14px 20px',
        backgroundColor: theme.palette.colors.darkBlack[400],
    },
    inputField: {
        flex: 1,
        '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            backgroundColor: theme.palette.colors.darkBlack[400],
        },
    },
    sendButton: {
        minWidth: '48px',
        height: '48px',
        borderRadius: '12px',
        background: `linear-gradient(90deg, ${theme.palette.colors.violet[400]} 0%, ${theme.palette.colors.violet[300]} 100%)`,
        color: theme.palette.white,
        '&:hover': {
            background: `linear-gradient(90deg, ${theme.palette.colors.violet[500]} 0%, ${theme.palette.colors.violet[400]} 100%)`,
        },
    },
}));

const ChatTALI = () => {
    const classes = useStyles();
    const [comment, setComment] = useState('');
    const [pendingConfirmation, setPendingConfirmation] = useState(null);
    const [confirming, setConfirming] = useState(false);
    const [loading, setLoading] = useState(false);
    const [history, setHistory] = useState([
        /*{ role: 'user', content: 'hola valero', order: 1 },
        { role: 'assistant', content: ' **Hola, ¿en qué puedo ayudarte hoy?**', order: 2 },
        { role: 'user', content: 'donde estudio', order: 3 },
        { role: 'assistant', content: '**en la universidad interamericana pará el desarrollo**', order: 4 },
        { role: 'user', content: 'cuantos semestres tengo', order: 5 },
        { role: 'assistant', content: '**tienes 6 semestres**', order: 6 },*/
    ]);
    const user = useSelector((state) => state.userReducer.user);
    const roles = useSelector((state) => state.permissionReducer.roles);
    const messagesEndRef = useRef(null);

    // Función para hacer scroll automático al final
    const scrollToBottom = () => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
    };

    // Efecto para hacer scroll cuando cambie el historial o el estado de carga
    useEffect(() => {
        scrollToBottom();
    }, [history, loading]);

    // ¿Por qué la respuesta del assistant y el error ponen el +2 en el order?
    // El +2 se usaba porque history.length no se actualiza inmediatamente tras setHistory (por el mensaje del usuario).
    // Pero esto puede causar problemas si hay concurrencia o si el estado no se actualiza como se espera.
    // Lo correcto es calcular el order en función del último mensaje en el historial, o usar una función de actualización.

    const CHAT_URL = 'https://y0kb3j4cnj.execute-api.us-east-1.amazonaws.com/prod/';

    const appendAssistantMessage = (content, variant = 'text', extra = {}) => {
        setHistory(prev => {
            const lastOrder = prev.length > 0 ? prev[prev.length - 1].order : 0;
            const assistantMessage = {
                role: 'assistant',
                content,
                variant,
                order: lastOrder + 1,
                ...extra,
            };
            return [...prev, assistantMessage];
        });
    };

    const handleChatResponse = (data) => {
        if (data.kind === 'message') {
            appendAssistantMessage(data.message);
            setPendingConfirmation(null);
            return;
        }
        if (data.kind === 'result') {
            appendAssistantMessage('', 'result', { tool: data.tool, result: data.result });
            setPendingConfirmation(null);
            return;
        }
        if (data.kind === 'confirmation_required') {
            appendAssistantMessage(data.message, 'confirmation');
            setPendingConfirmation(data.proposed);
            return;
        }
        appendAssistantMessage('Ocurrió un error al obtener la respuesta.');
        setPendingConfirmation(null);
    };

    const handleService = async () => {
        if (!comment.trim()) return;

        const currentComment = comment;
        setComment('');

        // Agregar mensaje del usuario al historial solo una vez
        const userMessage = {
            role: 'user',
            content: currentComment,
            order: history.length + 1
        };
        setHistory(prev => [...prev, userMessage]);
        
        // Scroll inmediato después de agregar el mensaje del usuario
        setTimeout(() => scrollToBottom(), 100);

        // Modo demo (solo desarrollo): `/demo <clave>` o `/demo all` inyecta
        // respuestas dummy sin llamar al backend, para validar el ResultRenderer.
        if (currentComment.trim().toLowerCase().startsWith('/demo')) {
            const key = currentComment.trim().split(/\s+/)[1];
            if (!key || key === 'help') {
                appendAssistantMessage('Modo demo. Usa `/demo <clave>` o `/demo all`. Claves: ' + DEMO_KEYS.join(', '));
                return;
            }
            if (key === 'all') {
                DEMO_KEYS.forEach((k) => handleChatResponse(DUMMY_RESPONSES[k]));
                return;
            }
            if (DUMMY_RESPONSES[key]) {
                handleChatResponse(DUMMY_RESPONSES[key]);
            } else {
                appendAssistantMessage('Clave de demo desconocida. Disponibles: ' + DEMO_KEYS.join(', '));
            }
            return;
        }

        try {
            setLoading(true);
            const response = await axios.post(
                CHAT_URL,
                {
                    "message": currentComment,
                    "person_id": user.person_id.toString(),
                    "roles": roles || [],
                }
            );
            handleChatResponse(response.data);
        } catch (error) {
            appendAssistantMessage("Ocurrió un error al obtener la respuesta.");
            setPendingConfirmation(null);
            console.error(error);
        } finally {
            setLoading(false);
        }
    }

    const handleConfirmWrite = async () => {
        if (!pendingConfirmation) return;
        setConfirming(true);
        setLoading(true);
        try {
            const response = await axios.post(
                CHAT_URL,
                {
                    "person_id": user.person_id.toString(),
                    "roles": roles || [],
                    "confirm": pendingConfirmation,
                }
            );
            handleChatResponse(response.data);
        } catch (error) {
            appendAssistantMessage("Ocurrió un error al confirmar la operación.");
            setPendingConfirmation(null);
            console.error(error);
        } finally {
            setConfirming(false);
            setLoading(false);
        }
    }
    return (
        <Box className={classes.root}>
            <Card className={classes.shell}>
                {/* Header con gradiente e identidad TALI */}
                <Box className={classes.header}>
                    <Box className={classes.headerAvatar}>
                        <Icon className="ri-cpu-line font-size-24 text-violet-300" />
                    </Box>
                    <Box>
                        <Typography variant="h4" className={classes.headerTitle}>Asistente Brightspace</Typography>
                        <Box className={classes.headerStatusRow}>
                            <span className={classes.statusDot} />
                            <Typography variant="caption" className={classes.headerSub}>En línea · Soporte D2L (Brightspace) </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* Cuerpo del chat */}
                <Box className={classes.container}>
                    <Box className={classes.chatContainer}>
                        {history
                            .slice()
                            .sort((a, b) => a.order - b.order)
                            .map((item) => {
                                const isUser = item.role === 'user';
                                if (isUser) {
                                    return (
                                        <Box key={item.order} className={clsx(classes.messageRow, classes.userRow)}>
                                            <Box className={clsx(classes.messageBubble, classes.userBubble)}>
                                                <Typography variant="body2">{item.content}</Typography>
                                            </Box>
                                        </Box>
                                    );
                                }
                                return (
                                    <Box key={item.order} className={clsx(classes.messageRow, classes.assistantRow)}>
                                        <Box className={classes.botAvatar}>
                                            <Icon className="ri-cpu-line font-size-18 text-white" />
                                        </Box>
                                        {item.variant === 'result' ? (
                                            <Box className={clsx(classes.messageBubble, classes.assistantBubble)}>
                                                <ResultRenderer tool={item.tool} result={item.result} />
                                            </Box>
                                        ) : item.variant === 'confirmation' ? (
                                            <Box className={classes.confirmBubble}>
                                                <Box className={classes.confirmHead}>
                                                    <Icon className="ri-error-warning-line font-size-16 text-warning-300" />
                                                    <Typography variant="caption" className={classes.confirmHeadLabel}>Confirmación requerida</Typography>
                                                </Box>
                                                <Typography variant="body2" className={classes.confirmMessage}>{item.content}</Typography>
                                                {pendingConfirmation && (
                                                    <Button
                                                        variant="contained"
                                                        className={classes.confirmButton}
                                                        onClick={handleConfirmWrite}
                                                        disabled={confirming}
                                                        startIcon={!confirming && <Icon className="ri-check-line font-size-16 text-white" />}
                                                    >
                                                        {confirming ? 'Confirmando...' : 'Confirmar'}
                                                    </Button>
                                                )}
                                            </Box>
                                        ) : (
                                            <Box className={clsx(classes.messageBubble, classes.assistantBubble)}>
                                                <Typography variant="caption" className={clsx(classes.messageLabel, classes.assistantLabel)}>
                                                    ASISTENTE BRIGHTSPACE
                                                </Typography>
                                                <Markdown>{item.content}</Markdown>
                                            </Box>
                                        )}
                                    </Box>
                                );
                            })}
                        {loading && (
                            <Box className={clsx(classes.messageRow, classes.assistantRow)}>
                                <Box className={classes.botAvatar}>
                                    <Icon className="ri-cpu-line font-size-18 text-white" />
                                </Box>
                                <Box className={clsx(classes.messageBubble, classes.assistantBubble, classes.typingBubble)}>
                                    <span className={clsx(classes.typingDot, classes.typingDot1)} />
                                    <span className={clsx(classes.typingDot, classes.typingDot2)} />
                                    <span className={clsx(classes.typingDot, classes.typingDot3)} />
                                </Box>
                            </Box>
                        )}
                        {/* Elemento invisible para el autoscroll */}
                        <div ref={messagesEndRef} />
                    </Box>

                    {/* Barra de entrada */}
                    <Box className={classes.inputContainer}>
                        <TextField
                            id="comment"
                            placeholder="Escribe tu mensaje..."
                            fullWidth
                            multiline
                            maxRows={3}
                            disabled={loading}
                            className={classes.inputField}
                            value={comment || ''}
                            variant="outlined"
                            onChange={(e) => setComment(e.target.value)}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    handleService();
                                }
                            }}
                        />
                        <Button
                            variant="contained"
                            className={classes.sendButton}
                            onClick={handleService}
                            disabled={!comment.trim() || loading}
                        >
                            <Icon className="ri-send-plane-2-line font-size-20 text-white" />
                        </Button>
                    </Box>
                </Box>
            </Card>
        </Box>
    );
};

const mapStateToProps = (state) => ({});

const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(ChatTALI);